import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { NotificationType } from '@app/shared/interfaces';
import { Reason } from '@app/shared/models/reason';
import { IntercomService } from '@app/shared/services/intercom.service';
import { LogService } from '@app/shared/services/logs/log.service';
import { RuleService } from '@app/shared/services/rule/rule.service';
import { ScheduledMessagesCancellerService } from '@app/shared/services/scheduled-messages-canceller/scheduled-messages-canceller.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';

@Component({
  standalone: false,
  selector: 'sbnb-log-detail',
  templateUrl: './log-detail.component.html',
  styleUrls: ['./log-detail.component.scss'],
})
export class LogDetailComponent implements OnInit {
  loading: boolean;
  logData: any;
  uuid: string;
  type: string;
  now: Date;
  restoring: boolean;
  sending: boolean;
  saving: boolean;
  cancelling: boolean;
  dirty = false;
  reason: Reason = null;

  user = this.authService.getUserDetails();
  originalMessage: string;
  isRejectingAiImprovement = false;

  constructor(
    private titleService: TitleService,
    private logService: LogService,
    private route: ActivatedRoute,
    private router: Router,
    private ruleService: RuleService,
    private cancellerService: ScheduledMessagesCancellerService,
    private authService: AuthenticationService,
    private toast: ToastNotificationsService,
    private readonly intercomService: IntercomService,
    private segmentIoService: SegmentIoService
  ) { }

  ngOnInit() {
    this.dirty = false;
    this.cancelling = false;
    this.sending = false;
    this.saving = false;
    this.now = new Date();

    this.titleService.manuallySet('Guest Experience Logs');
    this.loading = true;

    this.route.params.subscribe((params) => {
      this.uuid = params['uuid'];
      this.type = params['type'];

      this.fetchLogData();
    });
  }

  fetchLogData() {
    this.logService.getLog(this.type, this.uuid).subscribe((res) => {
      this.logData = res;
      this.loading = false;

      this.titleService.manuallySet(this.logData.title);

      if (!this.logData?.randomkey && !this.logData?.id && !this.logData.review_id) {
        this.toast.open('Log not found.', 'Dismiss', NotificationType.Error);

        this.router.navigate(['/gx/log']);
      }

      if (this.logData.type === 'answer') {
        this.logData.prepared_message = this.logData.reply;
      }

      this.originalMessage = this.logData.prepared_message;

      if (
        this.logData.ai_improvement?.message &&
        this.logData.ai_improvement?.rejected === false &&
        this.logData.ai_improvement?.edited === false
      ) {
        this.logData.prepared_message = this.logData.ai_improvement.message;
      }

      if (this.logData.sent_at) {
        this.logData.send_status = 'sent';
      }

      if (this.logData.cancelled_at) {
        this.logData.send_status = 'cancelled';
      }

      if (this.logData.scheduled_for && !this.logData.cancelled_at && !this.logData.sent_at) {
        this.logData.send_status = 'scheduled';
      }

      if (!this.logData.scheduled_for && !this.logData.cancelled_at && !this.logData.sent_at) {
        this.logData.send_status = 'prepared';
      }

      if (this.logData.failed) {
        this.logData.send_status = 'failed';
      }

      if (this.logData.closed) {
        this.logData.send_status = 'detected';
      }

      if (this.logData.send_status === 'cancelled' && new Date(this.logData.scheduled_for) > this.now) {
        this.logData.restore = true;
      }

      if (this.logData.scheduled_for) {
        this.logData.scheduled_day = this.logData.scheduled_for.substr(0, 10);

        this.logData.scheduled_time_hours = this.logData.scheduled_for.substr(11, 2);

        this.logData.scheduled_time_minutes = this.logData.scheduled_for.substr(14, 2);
      }

      this.reason = this.logService.resolveReasons(this.logData.reasons);

      const temp = this.logData.scheduled_for_label?.split(' ');
      if (temp?.length) {
        this.logData.scheduled_timezone = temp[temp.length - 1];
      }

      this.logData.origDate = this.logData.scheduled_day;
      this.logData.origHour = this.logData.scheduled_time_hours;
      this.logData.origMin = this.logData.scheduled_time_minutes;
    });
  }

  restoreClicked() {
    this.restoring = true;
    this.logService.restoreLog(this.uuid, this.type).subscribe((res) => {
      this.restoring = false;

      this.ngOnInit();
    });
  }

  undoAiImprovement() {
    this.isRejectingAiImprovement = true;
    this.logData.prepared_message = this.originalMessage;
  }

  sendMessage(sendNow = true) {
    this.dirty = false;

    if (sendNow) {
      this.sending = true;
    } else {
      this.saving = true;
    }

    if (this.type === 'scheduled' || this.type === 'activities' || this.type === 'questions') {
      let scheduledTimeUpdated = false;

      if (
        (this.type === 'scheduled' && this.logData.origDate !== this.logData.scheduled_day) ||
        this.logData.origHour !== this.logData.scheduled_time_hours ||
        this.logData.origMin !== this.logData.scheduled_time_minutes
      ) {
        scheduledTimeUpdated = true;
      }

      this.ruleService
        .sendMessage(
          this.uuid,
          this.logData.prepared_message,
          sendNow,
          this.type,
          scheduledTimeUpdated ? this.logData.scheduled_day : null,
          scheduledTimeUpdated ? this.logData.scheduled_time_hours : null,
          scheduledTimeUpdated ? this.logData.scheduled_time_minutes : null,
          this.isRejectingAiImprovement
        )
        .subscribe(
          (res) => {
            this.sending = false;
            this.saving = false;

            if (res.errors) {
              console.log(res.errors);
              this.ngOnInit();
            } else {
              this.logData.send_status = 'sent';
              this.ngOnInit();
            }
          },
          (error) => {
            this.ngOnInit();
          }
        );
    }

    if (this.type === 'reviews') {
      if (this.logData.review.category_tags) {
        this.segmentIoService.track(SegmentEvent.ReviewTagsSaved, {review_id : this.uuid });
      }

      this.ruleService
        .sendReview({
          reviewId: this.uuid,
          message: this.logData.prepared_message,
          private_feedback: this.logData.private_feedback,
          grades: this.logData.review.grades,
          send_now: sendNow,
          bad_review: this.logData.review.bad_review,
          cleanliness_tags: Array.from(this.logData.review.category_tags.cleanliness_tags),
          respect_house_rules_tags: Array.from(this.logData.review.category_tags.respect_house_rules_tags),
          communication_tags: Array.from(this.logData.review.category_tags.communication_tags),
        })
        .subscribe((res) => {
          if (!this.isBadReview()) {
            this.ngOnInit();
            return;
          } else {
            this.cancellerService.checkAndOpenScheduledMessageCanceller(
              this.logData.reservation_code,
              'review_marked_bad',
              this.logData.guest.name
            );
          }

          this.ngOnInit();
        });
    }
  }

  cancelClicked() {
    this.cancelling = true;

    if (this.type === 'reviews') {
      this.ruleService.deleteSingleReview(this.uuid).subscribe((res) => {
        this.cancellerService.checkAndOpenScheduledMessageCanceller(
          this.logData.reservation_code,
          'review_deleted',
          this.logData.guest.name
        );

        this.ngOnInit();
      });
    } else {
      this.ruleService.deleteSingleMessage(this.uuid, this.type).subscribe((res) => {
        this.ngOnInit();
      });
    }
  }

  openThread(threadUuid: string) {
    this.router.navigate(['/inbox/thread', threadUuid]);
  }

  isBadReview() {
    if (this.type !== 'reviews') {
      return false;
    }

    if (this.type === 'reviews') {
      return this.logData.review.bad_review;
    }
  }

  /**
   * Returns a display friendly label based on the Type of log (Review, Message, etc.)
   */
  getTypeLabel(): string {
    switch (this.type) {
      case 'reviews':
        return 'review';
        break;

      default:
        return 'message';
        break;
    }
  }

  reportProblem() {
    const messageText = `Please investigate an issue I'm having with Guest Experience log report ${(window as any).location.href
      }`;
    this.intercomService.showNewMessage(messageText);
  }

  newDateSelected($event) {
    this.dirty = true;
    this.logData.scheduled_day = $event;
  }

  newTimeSelected($event) {
    this.dirty = true;
    this.logData.scheduled_time_hours = $event.hours;
    this.logData.scheduled_time_minutes = $event.minutes;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event): void {
    if (this.dirty) {
      event.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
      // Chrome requires returnValue to be set
      event.returnValue = '';
    }
  }
}
